import React from 'react';
import ReactDOM from 'react-dom';
import API_BASE_URL from '../config';
import styles from '../css-isolated/hr_info.module.css';
import authFetch from '../utils/authFetch';
import { lettersOnly, numbersWithDash } from '../utils/inputValidation';

// Stateless, wizard-compatible HR Info step
function HRInfo({ values = {}, onChange, onSubmit, isDarkMode = false, openDatePicker, activeSection: externalActiveSection, setActiveSection: externalSetActiveSection }) {
  
  // Location data states
  const [regions, setRegions] = React.useState([]);
  const [provinces, setProvinces] = React.useState([]);
  const [municipalities, setMunicipalities] = React.useState([]);
  const [countries, setCountries] = React.useState([]);
  const [selectedRegionCode, setSelectedRegionCode] = React.useState('');
  const [selectedProvinceCode, setSelectedProvinceCode] = React.useState('');
  
  // Country search/filter states
  const [countrySearch, setCountrySearch] = React.useState('');
  const [showCountryDropdown, setShowCountryDropdown] = React.useState(false);
  const countryDropdownRef = React.useRef(null);
  
  // Philippine Criminal Cases with specific sections and RA numbers
  const philippineCriminalCases = [
    // RA 9165 - Comprehensive Dangerous Drugs Act of 2002
    'Viol of Sec 5 RA 9165 (Sale of Dangerous Drugs)',
    'Viol of Sec 11 RA 9165 (Possession of Dangerous Drugs)',
    'Viol of Sec 5 and 11 RA 9165',
    'Viol of Sec 12 RA 9165 (Possession of Equipment/Paraphernalia)',
    'Viol of Sec 13 RA 9165 (Possession During Party)',
    'Viol of Sec 14 RA 9165 (Possession of Equipment/Instrument)',
    'Viol of Sec 15 RA 9165 (Use of Dangerous Drugs)',
    'Viol of Sec 16 RA 9165 (Cultivation/Culture of Dangerous Drugs)',
    
    // RA 10591 - Comprehensive Firearms and Ammunition Regulation Act
    'Viol of Sec 28 RA 10591 (Illegal Possession of Firearms)',
    'Viol of Sec 29 RA 10591 (Illegal Possession of Ammunition)',
    
    // RA 9262 - Anti-Violence Against Women and Their Children Act
    'Viol of RA 9262 (VAWC)',
    'Viol of Sec 5(a) RA 9262 (Physical Violence)',
    'Viol of Sec 5(e) RA 9262 (Economic Abuse)',
    'Viol of Sec 5(h) RA 9262 (Psychological Violence)',
    
    // RA 7610 - Special Protection of Children Against Abuse, Exploitation and Discrimination Act
    'Viol of RA 7610 (Child Abuse)',
    'Viol of Sec 10(a) RA 7610 (Child Abuse)',
    
    // RA 9344 - Juvenile Justice and Welfare Act
    'Viol of RA 9344 (Juvenile Delinquency)',
    
    // RA 8049 - Anti-Hazing Law
    'Viol of RA 8049 (Hazing)',
    
    // BP 22 - Bouncing Checks Law
    'Viol of BP 22 (Bouncing Checks)',
    
    // RA 3815 - Revised Penal Code
    'Murder (Art 248 RPC)',
    'Homicide (Art 249 RPC)',
    'Parricide (Art 246 RPC)',
    'Infanticide (Art 255 RPC)',
    'Frustrated Murder (Art 248 RPC)',
    'Frustrated Homicide (Art 249 RPC)',
    'Physical Injuries (Art 263 RPC)',
    'Serious Physical Injuries (Art 263 RPC)',
    'Less Serious Physical Injuries (Art 265 RPC)',
    'Slight Physical Injuries (Art 266 RPC)',
    'Rape (Art 266-A RPC)',
    'Rape by Sexual Assault (Art 266-A RPC)',
    'Acts of Lasciviousness (Art 336 RPC)',
    'Qualified Rape',
    'Robbery (Art 293 RPC)',
    'Robbery with Homicide (Art 294 RPC)',
    'Robbery with Rape (Art 294 RPC)',
    'Robbery with Physical Injuries (Art 294 RPC)',
    'Robbery in Band (Art 296 RPC)',
    'Theft (Art 308 RPC)',
    'Qualified Theft (Art 310 RPC)',
    'Estafa (Art 315 RPC)',
    'Syndicated Estafa',
    'Kidnapping (Art 267 RPC)',
    'Kidnapping for Ransom (Art 267 RPC)',
    'Illegal Detention (Art 267 RPC)',
    'Serious Illegal Detention (Art 267 RPC)',
    'Carnapping (RA 6539)',
    'Qualified Carnapping',
    'Arson (Art 320 RPC)',
    'Destructive Arson (PD 1613)',
    'Malicious Mischief (Art 327 RPC)',
    'Grave Threats (Art 282 RPC)',
    'Light Threats (Art 285 RPC)',
    'Grave Coercion (Art 286 RPC)',
    'Unjust Vexation (Art 287 RPC)',
    'Trespassing to Dwelling (Art 280 RPC)',
    'Qualified Trespassing (Art 280 RPC)',
    'Alarm and Scandal (Art 155 RPC)',
    'Falsification of Public Documents (Art 171 RPC)',
    'Falsification of Private Documents (Art 172 RPC)',
    'Use of Falsified Documents (Art 172 RPC)',
    'Perjury (Art 183 RPC)',
    'Libel (Art 353 RPC)',
    'Oral Defamation/Slander (Art 358 RPC)',
    'Grave Slander (Art 358 RPC)',
    'Slight Slander (Art 358 RPC)',
    'Bigamy (Art 349 RPC)',
    'Adultery (Art 333 RPC)',
    'Concubinage (Art 334 RPC)',
    'Seduction (Art 337 RPC)',
    'Qualified Seduction (Art 337 RPC)',
    'Abduction (Art 342 RPC)',
    'Direct Assault (Art 148 RPC)',
    'Indirect Assault (Art 149 RPC)',
    'Resistance and Disobedience (Art 151 RPC)',
    'Tumults and Other Disturbances (Art 153 RPC)',
    'Unlawful Arrest (Art 269 RPC)',
    'Arbitrary Detention (Art 124 RPC)',
    'Bribery (Art 210 RPC)',
    'Corruption of Public Officials (Art 212 RPC)',
    'Swindling/Estafa (Art 315 RPC)',
    
    // RA 3019 - Anti-Graft and Corrupt Practices Act
    'Viol of RA 3019 (Graft and Corruption)',
    'Viol of Sec 3(e) RA 3019',
    
    // RA 9165 Additional
    'Drug Trafficking',
    'Maintaining a Drug Den (Sec 6 RA 9165)',
    
    // RA 10175 - Cybercrime Prevention Act
    'Viol of RA 10175 (Cybercrime)',
    'Cyberlibel (RA 10175)',
    'Online Sexual Exploitation',
    'Computer-Related Fraud',
    'Illegal Access',
    
    // RA 9208 - Anti-Trafficking in Persons Act
    'Viol of RA 9208 (Human Trafficking)',
    'Qualified Trafficking in Persons',
    
    // RA 9775 - Anti-Child Pornography Act
    'Viol of RA 9775 (Child Pornography)',
    
    // RA 11313 - Safe Spaces Act (Bawal Bastos Law)
    'Viol of RA 11313 (Gender-Based Sexual Harassment)',
    
    // Other Common Cases
    'Illegal Gambling (PD 1602)',
    'Illegal Recruitment (RA 8042)',
    'Qualified Illegal Recruitment',
    'Anti-Fencing Law (PD 1612)',
    'Vote Buying (RA 9369)',
    'Election Offense',
    'Cattle Rustling (PD 533)',
    'Illegal Logging (PD 705)',
    'Illegal Fishing (RA 8550)',
    'Wildlife Trafficking (RA 9147)',
    'Rebellion (Art 134 RPC)',
    'Sedition (Art 139 RPC)',
    'Inciting to Sedition (Art 142 RPC)',
    'Coup d\'Etat (Art 134-A RPC)',
    'Tax Evasion (NIRC)',
    'Smuggling (CMTA)',
    'Others'
  ];
  
  // Helper function to format date for display (MM/DD/YYYY)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'Select Date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Select Date';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) {
      return 0;
    }
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate.getTime())) {
      return 0;
    }
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Handler to update wizard state for all fields
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    onChange && onChange({
      ...values,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handler for case dropdown - allows switching to input when "Others" is selected
  const handleCaseChange = (e) => {
    const value = e.target.value;
    onChange && onChange({
      ...values,
      case: value,
      isOtherCase: value === 'Others'
    });
  };

  // Handler for manual case input when "Others" is selected
  const handleOtherCaseInput = (e) => {
    onChange && onChange({
      ...values,
      case: e.target.value
    });
  };

  // Fetch regions on component mount
  React.useEffect(() => {
    authFetch('https://psgc.gitlab.io/api/regions/')
      .then(res => res.json())
      .then(data => {
        setRegions(data || []);
      })
      .catch(err => console.error('Error fetching regions:', err));

    // Use complete hardcoded list of all countries (195+)
    const allCountries = [
      'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
      'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain',
      'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan',
      'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei Darussalam',
      'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon',
      'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia',
      'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
      'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
      'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
      'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana',
      'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
      'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland',
      'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati',
      'Korea, North', 'Korea, South', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia',
      'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
      'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands',
      'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia',
      'Montenegro', 'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nauru', 'Nepal',
      'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Macedonia',
      'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea',
      'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
      'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia',
      'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe',
      'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
      'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Sudan',
      'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
      'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga',
      'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda',
      'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States of America',
      'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
      'Yemen', 'Zambia', 'Zimbabwe'
    ];
    setCountries(allCountries);
  }, []);

  // Restore region code and load provinces when component mounts with existing region value
  React.useEffect(() => {
    if (values.region && regions.length > 0) {
      // Find the region object that matches the saved region name
      const regionObj = regions.find(r => r.name === values.region);
      if (regionObj) {
        setSelectedRegionCode(regionObj.code);
        
        // Load provinces for this region
        authFetch(`https://psgc.gitlab.io/api/regions/${regionObj.code}/provinces/`)
          .then(res => res.json())
          .then(data => {
            setProvinces(data || []);
          })
          .catch(err => console.error('Error loading provinces:', err));
      }
    }
  }, [values.region, regions]);

  // Restore province code and load municipalities when component mounts with existing province value
  React.useEffect(() => {
    if (values.province && provinces.length > 0 && selectedRegionCode) {
      // Find the province object that matches the saved province name
      const provinceObj = provinces.find(p => p.name === values.province);
      if (provinceObj) {
        setSelectedProvinceCode(provinceObj.code);
        
        // Load municipalities for this province
        authFetch(`https://psgc.gitlab.io/api/provinces/${provinceObj.code}/cities-municipalities/`)
          .then(res => res.json())
          .then(data => {
            setMunicipalities(data || []);
          })
          .catch(err => console.error('Error loading municipalities:', err));
      }
    }
  }, [values.province, provinces, selectedRegionCode]);

  // Fetch provinces when region changes
  const handleRegionChange = (e) => {
    const regionCode = e.target.value;
    const regionName = regions.find(r => r.code === regionCode)?.name || '';
    
    setSelectedRegionCode(regionCode);
    setSelectedProvinceCode('');
    setProvinces([]);
    setMunicipalities([]);
    
    onChange && onChange({
      ...values,
      region: regionName,
      province: '',
      municipality: ''
    });

    if (regionCode) {
      // NCR (National Capital Region) has no provinces, fetch cities directly
      if (regionCode === '130000000') {
        authFetch(`https://psgc.gitlab.io/api/regions/${regionCode}/cities-municipalities/`)
          .then(res => res.json())
          .then(data => {
            setMunicipalities(data || []);
            // Set province as "Metro Manila" for NCR
            onChange && onChange({
              ...values,
              region: regionName,
              province: 'Metro Manila',
              municipality: ''
            });
          })
          .catch(err => console.error('Error fetching NCR cities:', err));
      } else {
        authFetch(`https://psgc.gitlab.io/api/regions/${regionCode}/provinces/`)
          .then(res => res.json())
          .then(data => {
            setProvinces(data || []);
          })
          .catch(err => console.error('Error fetching provinces:', err));
      }
    }
  };

  // Fetch municipalities when province changes
  const handleProvinceChange = (e) => {
    const provinceCode = e.target.value;
    const provinceName = provinces.find(p => p.code === provinceCode)?.name || '';
    
    setSelectedProvinceCode(provinceCode);
    setMunicipalities([]);
    
    onChange && onChange({
      ...values,
      province: provinceName,
      municipality: ''
    });

    if (provinceCode) {
      authFetch(`https://psgc.gitlab.io/api/provinces/${provinceCode}/cities-municipalities/`)
        .then(res => res.json())
        .then(data => {
          setMunicipalities(data || []);
        })
        .catch(err => console.error('Error fetching municipalities:', err));
    }
  };

  // Handle municipality change
  const handleMunicipalityChange = (e) => {
    const municipalityName = municipalities.find(m => m.code === e.target.value)?.name || '';
    
    onChange && onChange({
      ...values,
      municipality: municipalityName
    });
  };

  // Handle country search input - only update search, don't auto-select
  const handleCountrySearchChange = (e) => {
    const newValue = e.target.value;
    setCountrySearch(newValue);
    setShowCountryDropdown(newValue.trim().length > 0);
    
    // Clear the country value in the form when user is typing/editing
    // This prevents auto-completion - only set when explicitly clicked from dropdown
    if (newValue !== values.country) {
      onChange && onChange({
        ...values,
        country: '' // Clear the country until user explicitly selects
      });
    }
  };

  // Handle country selection from filtered list
  const handleCountrySelect = (country) => {
    // Only update if explicitly clicked (not from typing)
    setCountrySearch(country);
    setShowCountryDropdown(false);
    
    // Show info modal if a non-Philippine country is selected
    const isPhilippines = /^philippines$/i.test(country.trim());
    if (!isPhilippines) {
      setShowForeignCountryModal(true);
    }

    // Update form data after a brief delay to ensure state settles
    setTimeout(() => {
      onChange && onChange({
        ...values,
        country: country
      });
    }, 0);
  };

  // Filter countries based on search input
  const getFilteredCountries = () => {
    if (!countrySearch.trim()) {
      return countries;
    }
    const searchLower = countrySearch.toLowerCase();
    return countries.filter(country => 
      country.toLowerCase().includes(searchLower)
    );
  };

  // Update country search when values.country changes (for edit mode and clear form)
  React.useEffect(() => {
    setCountrySearch(values.country || '');
  }, [values.country]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if the click target is NOT within the country input group
      const inputElement = countryDropdownRef.current?.querySelector('input[placeholder*="Search country"]');
      const isClickOnInput = inputElement && inputElement.contains(event.target);
      const isClickOnDropdown = event.target.closest('div[style*="position: fixed"]');
      
      if (!isClickOnInput && !isClickOnDropdown) {
        setShowCountryDropdown(false);
      }
    };

    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCountryDropdown, countryDropdownRef]);

  // Image upload state and handler
  const [photoPreview, setPhotoPreview] = React.useState(null);
  const [showCamera, setShowCamera] = React.useState(false);
  const [stream, setStream] = React.useState(null);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  
  // Error modal states
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  // Foreign country info modal state
  const [showForeignCountryModal, setShowForeignCountryModal] = React.useState(false);
  
  // Underage confirmation modal states
  const [showUnderageModal, setShowUnderageModal] = React.useState(false);
  const [pendingDateOfBirth, setPendingDateOfBirth] = React.useState(null);
  const [calculatedAge, setCalculatedAge] = React.useState(0);

  // Active section state (1 = Personal Info, 2 = Commitment Info, 3 = Case & Contact Info)
  // Use external state if provided (from HRWizard), otherwise use internal state
  const [internalActiveSection] = React.useState(1);
  const activeSection = externalActiveSection !== undefined ? externalActiveSection : internalActiveSection;

  // Debug: Log when dateOfBirth changes
  React.useEffect(() => {
  }, [values.dateOfBirth]);

  // Debug: Log when underage modal state changes
  React.useEffect(() => {
  }, [showUnderageModal, pendingDateOfBirth, calculatedAge]);

  // Set default values for Name of Jail and Origin Lock-up if empty
  React.useEffect(() => {
    const updates = {};
    if (values.nameOfJail === undefined || values.nameOfJail === '') {
      updates.nameOfJail = 'Santa Cruz District Jail';
    }
    if (values.originLockup === undefined || values.originLockup === '') {
      updates.originLockup = 'Santa Cruz MPS';
    }
    if (Object.keys(updates).length > 0) {
      onChange && onChange({
        ...values,
        ...updates
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Update photo preview when photo data changes (for edit mode and clear form)
  React.useEffect(() => {
    const photoData = values.photoUrl || values.photo_of_pdl || values.photoFile;
    if (photoData) {
      // If it's a file path from database, construct the full URL
      if (typeof photoData === 'string' && photoData.startsWith('pdl_photos/')) {
        setPhotoPreview(`${API_BASE_URL}/api/storage/${photoData}`);
      } 
      // If it's already a data URL or full URL, use as is
      else if (typeof photoData === 'string') {
        setPhotoPreview(photoData);
      }
      // If it's a File object, convert to data URL
      else if (photoData instanceof File) {
        const reader = new FileReader();
        reader.onloadend = () => setPhotoPreview(reader.result);
        reader.readAsDataURL(photoData);
      }
    } else {
      // Reset photo preview when photo is cleared
      setPhotoPreview(null);
    }
  }, [values.photoUrl, values.photo_of_pdl, values.photoFile]);

  // Helper function to compress and resize image
  const compressImage = (file, maxSizeKB = 400) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions (max 800px on longest side)
          const maxDimension = 800;
          if (width > height && width > maxDimension) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else if (height > maxDimension) {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Try different quality levels to get under maxSizeKB
          let quality = 0.8;
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Keep reducing quality until we're under the size limit
          while (dataUrl.length > maxSizeKB * 1024 * 1.37 && quality > 0.1) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          
          resolve(dataUrl);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      // Check if it's an image file
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select a valid image file (JPG, PNG, etc.).');
        setShowErrorModal(true);
        e.target.value = ''; // Clear the input
        return;
      }

      try {
        // Compress the image
        const compressedDataUrl = await compressImage(file, 400);
        
        setPhotoPreview(compressedDataUrl);
        onChange && onChange({
          ...values,
          photoUrl: compressedDataUrl,
          photo_of_pdl: compressedDataUrl,
          photoFile: file
        });
      } catch (error) {
        console.error('Error processing photo:', error);
        if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
          setErrorMessage('Storage quota exceeded. Please try clearing your browser storage or use a smaller image.');
        } else {
          setErrorMessage('Failed to process the image. Please try a different image.');
        }
        setShowErrorModal(true);
        e.target.value = ''; // Clear the input
      }
    }
  };

  // Drag and drop support
  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) {
      // Check if it's an image file
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select a valid image file (JPG, PNG, etc.).');
        setShowErrorModal(true);
        return;
      }

      try {
        // Compress the image
        const compressedDataUrl = await compressImage(file, 400);
        
        setPhotoPreview(compressedDataUrl);
        onChange && onChange({
          ...values,
          photoUrl: compressedDataUrl,
          photo_of_pdl: compressedDataUrl,
          photoFile: file
        });
      } catch (error) {
        console.error('Error processing photo:', error);
        if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
          setErrorMessage('Storage quota exceeded. Please try clearing your browser storage or use a smaller image.');
        } else {
          setErrorMessage('Failed to process the image. Please try a different image.');
        }
        setShowErrorModal(true);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Camera functionality
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      setStream(mediaStream);
      setShowCamera(true);
      
      // Wait for video element to be ready
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (err) {
      console.error('Error accessing camera:', err);
      alert('Unable to access camera. Please make sure you have granted camera permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      try {
        canvas.toBlob((blob) => {
          const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
          const dataUrl = canvas.toDataURL('image/jpeg');
          
          try {
            setPhotoPreview(dataUrl);
            onChange && onChange({
              ...values,
              photoUrl: dataUrl,
              photo_of_pdl: dataUrl,
              photoFile: file
            });
            stopCamera();
          } catch (error) {
            console.error('Error saving captured photo:', error);
            if (error.name === 'QuotaExceededError' || error.message.includes('quota')) {
              setErrorMessage('Storage quota exceeded. Please try reducing the image quality or clear browser storage.');
            } else {
              setErrorMessage('Failed to save captured photo. Please try again.');
            }
            setShowErrorModal(true);
            stopCamera();
          }
        }, 'image/jpeg', 0.95);
      } catch (error) {
        console.error('Error capturing photo:', error);
        setErrorMessage('Failed to capture photo. Please try again.');
        setShowErrorModal(true);
        stopCamera();
      }
    }
  };

  // Cleanup camera on unmount
  React.useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  return (
    <div className={`${styles['hr-wizard-container']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
      {/* Two Column Layout */}
      <div className={styles.twoColumnLayout}>
          {/* Left Column - Form Fields */}
          <div className={styles.leftColumn}>

            {/* Personal Information Section with Photo */}
            {activeSection === 1 && (
            <div className={styles.compactSection}>
              <div className={styles.sectionTitleCompact}>
                <div className={styles.personIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                Personal Information
              </div>
              <div className={styles.personalInfoWithPhoto}>
                {/* Photo Section */}
                <div className={styles.photoSectionSide}>
                  <div
                    className={styles.photoUploadAreaSmall}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                  >
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        alt="PDL Preview"
                        className={styles.photoPreviewImg}
                      />
                    ) : (
                      <div className={styles.photoContent}>
                        <div className={styles.photoIcon}>📷</div>
                        <div className={styles.photoText}>Click buttons below</div>
                      </div>
                    )}
                    <input
                      id="photo-upload-input"
                      type="file"
                      accept="image/*"
                      className={styles.photoUploadInput}
                      onChange={handlePhotoChange}
                    />
                  </div>
                  
                  {/* Photo Action Buttons */}
                  <div className={styles.photoActionButtons}>
                    <button
                      type="button"
                      className={styles.photoUploadBtn}
                      onClick={() => document.getElementById('photo-upload-input').click()}
                    >
                      <span className={styles.btnIcon}>📁</span>
                      Upload
                    </button>
                    <button
                      type="button"
                      className={styles.photoCameraBtn}
                      onClick={startCamera}
                    >
                      <span className={styles.btnIcon}>📷</span>
                      Camera
                    </button>
                  </div>
                </div>
                
                {/* Form Fields */}
                <div className={styles.compactFormGrid}>
                {/* First Row - Name Fields */}
                <div className={styles.nameRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Surname</label>
                    <input type="text" name="surname" value={values.surname || ''} onChange={handleChange} onKeyDown={lettersOnly} className={styles.compactInput} />
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>First Name</label>
                    <input type="text" name="firstName" value={values.firstName || ''} onChange={handleChange} onKeyDown={lettersOnly} className={styles.compactInput} />
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={`${styles.compactLabel} ${styles.optionalLabel}`}>Middle Name</label>
                    <input type="text" name="middleName" value={values.middleName || ''} onChange={handleChange} onKeyDown={lettersOnly} className={styles.compactInput} />
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={`${styles.compactLabel} ${styles.optionalLabel}`}>AKA (Also Known As)</label>
                    <input type="text" name="aka" value={values.aka || ''} onChange={handleChange} onKeyDown={lettersOnly} className={styles.compactInput} placeholder="Also Known As" />
                  </div>
                </div>
                
                {/* Second Row - 4 columns */}
                <div className={styles.nameRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Sex</label>
                    <select name="sex" value={values.sex || ''} onChange={handleChange} className={styles.compactInput}>
                      <option value="">Select Sex</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                    </select>
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Civil Status</label>
                    <select name="civilStatus" value={values.civilStatus || ''} onChange={handleChange} className={styles.compactInput}>
                      <option value="">Select Civil Status</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Widowed">Widowed</option>
                      <option value="Separated">Separated</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Live-in">Live-in</option>
                    </select>
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Date of Birth</label>
                    <div 
                      className={styles.datePickerField}
                      onClick={() => {
                        if (openDatePicker) {
                          openDatePicker(values.dateOfBirth || '', (selectedDate) => {
                            // Calculate age from selected date
                            const age = calculateAge(selectedDate);
                            // If under 18, show confirmation modal
                            if (age < 18 && age >= 0) {
                              // Use setTimeout with longer delay to ensure date picker fully closes and unmounts
                              setTimeout(() => {
                                setPendingDateOfBirth(selectedDate);
                                setCalculatedAge(age);
                                setShowUnderageModal(true);
                              }, 400);
                            } else {
                              // If 18 or older, directly update
                              onChange && onChange({
                                ...values,
                                dateOfBirth: selectedDate
                              });
                            }
                          }, 'today'); // Restrict to today or earlier
                        }
                      }}
                    >
                      <span className={styles.datePickerIcon}>📅</span>
                      <span className={values.dateOfBirth ? styles.datePickerValue : styles.datePickerPlaceholder}>
                        {formatDateForDisplay(values.dateOfBirth)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Educational Attainment</label>
                    <select name="educationalAttainment" value={values.educationalAttainment || ''} onChange={handleChange} className={styles.compactInput}>
                      <option value="">Select Educational Attainment</option>
                      <option value="No Formal Education">No Formal Education</option>
                      <option value="Elementary Undergraduate">Elementary Undergraduate</option>
                      <option value="Elementary Graduate">Elementary Graduate</option>
                      <option value="High School Undergraduate">High School Undergraduate</option>
                      <option value="High School Graduate">High School Graduate</option>
                      <option value="Senior High School Undergraduate">Senior High School Undergraduate</option>
                      <option value="Senior High School Graduate">Senior High School Graduate</option>
                      <option value="Vocational/Technical Course">Vocational/Technical Course</option>
                      <option value="College Undergraduate">College Undergraduate</option>
                      <option value="College Graduate">College Graduate</option>
                      <option value="Post Graduate (Masters)">Post Graduate (Masters)</option>
                      <option value="Post Graduate (Doctorate)">Post Graduate (Doctorate)</option>
                    </select>
                  </div>
                </div>
                
                {/* Third Row - Address fields in 4 columns */}
                <div className={styles.nameRow}>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Country</label>
                    <div ref={countryDropdownRef} style={{ position: 'relative', width: '100%' }}>
                      <input 
                        type="text"
                        placeholder="Search country..."
                        value={countrySearch}
                        onChange={handleCountrySearchChange}
                        onFocus={() => { if (countrySearch.trim().length > 0) setShowCountryDropdown(true); }}
                        onKeyDown={(e) => {
                          // Don't auto-select on Enter or other keys
                          if (e.key === 'Escape') {
                            setShowCountryDropdown(false);
                          }
                        }}
                        className={styles.compactInput}
                        style={{ width: '100%' }}
                      />
                      {showCountryDropdown && ReactDOM.createPortal(
                        <div 
                          className={`${styles.countryDropdown} ${!isDarkMode ? styles.lightMode : ''}`}
                          style={{
                            top: countryDropdownRef.current ? 
                              (countryDropdownRef.current.getBoundingClientRect().bottom + 2) : 0,
                            left: countryDropdownRef.current ? 
                              countryDropdownRef.current.getBoundingClientRect().left : 0,
                            width: countryDropdownRef.current ? 
                              countryDropdownRef.current.getBoundingClientRect().width : 'auto'
                          }}>
                          {getFilteredCountries().length > 0 ? (
                            getFilteredCountries().map((country, index) => (
                              <div
                                key={`country-${country}-${index}`}
                                className={`${styles.countryItem} ${countrySearch === country ? styles.countryItemSelected : ''}`}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleCountrySelect(country);
                                }}
                              >
                                {country}
                              </div>
                            ))
                          ) : (
                            <div className={styles.noCountriesMessage}>
                              No countries found
                            </div>
                          )}
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Region</label>
                    <select 
                      name="region" 
                      value={selectedRegionCode} 
                      onChange={handleRegionChange} 
                      className={styles.compactInput}
                      disabled={values.country !== 'Philippines'}
                      title={values.country !== 'Philippines' ? 'Only available for Philippines' : ''}
                    >
                      <option value="">Select Region</option>
                      {values.country === 'Philippines' && regions.map(region => (
                        <option key={region.code} value={region.code}>
                          {region.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Province</label>
                    {selectedRegionCode === '130000000' ? (
                      <input 
                        type="text" 
                        name="province" 
                        value="Metro Manila" 
                        className={styles.compactInput}
                        disabled
                        readOnly
                      />
                    ) : (
                      <select 
                        name="province" 
                        value={selectedProvinceCode} 
                        onChange={handleProvinceChange} 
                        className={styles.compactInput}
                        disabled={!selectedRegionCode || values.country !== 'Philippines'}
                        title={values.country !== 'Philippines' ? 'Only available for Philippines' : ''}
                      >
                        <option value="">Select Province</option>
                        {values.country === 'Philippines' && provinces.map(province => (
                          <option key={province.code} value={province.code}>
                            {province.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className={styles.inputGroup}>
                    <label className={styles.compactLabel}>Municipality/City</label>
                    <select 
                      name="municipality" 
                      value={municipalities.find(m => m.name === values.municipality)?.code || ''} 
                      onChange={handleMunicipalityChange} 
                      className={styles.compactInput}
                      disabled={(selectedRegionCode === '130000000' ? !municipalities.length : !selectedProvinceCode) || values.country !== 'Philippines'}
                      title={values.country !== 'Philippines' ? 'Only available for Philippines' : ''}
                    >
                      <option value="">Select Municipality/City</option>
                      {values.country === 'Philippines' && municipalities.map(municipality => (
                        <option key={municipality.code} value={municipality.code}>
                          {municipality.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              </div>
            </div>
            )}

            {/* Commitment Information + Case & Contact together */}
            {activeSection === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className={styles.compactSection}>
                <div className={styles.sectionTitleCompact}>
                  <div className={styles.calendarIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  Commitment Information
                </div>
                <div className={styles.compactFormGrid}>
                  {/* First Row - All commitment fields in 4 columns */}
                  <div className={styles.nameRow}>
                    <div className={styles.inputGroup}>
                      <label className={styles.compactLabel}>Date of Commitment</label>
                      <div 
                        className={styles.datePickerField}
                        onClick={() => {
                          if (openDatePicker) {
                            openDatePicker(values.dateOfCommitment || '', (selectedDate) => {
                              onChange && onChange({
                                ...values,
                                dateOfCommitment: selectedDate
                              });
                            }, 'today'); // Restrict to today or earlier
                          }
                        }}
                      >
                        <span className={styles.datePickerIcon}>📅</span>
                        <span className={values.dateOfCommitment ? styles.datePickerValue : styles.datePickerPlaceholder}>
                          {formatDateForDisplay(values.dateOfCommitment)}
                        </span>
                      </div>
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={styles.compactLabel}>Name of Jail</label>
                      <input type="text" name="nameOfJail" value={values.nameOfJail ?? ''} onChange={handleChange} className={styles.compactInput} />
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={styles.compactLabel}>Origin Lock-up</label>
                      <input type="text" name="originLockup" value={values.originLockup ?? ''} onChange={handleChange} className={styles.compactInput} />
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.compactLabel} ${styles.optionalLabel}`}>PhilHealth No.</label>
                      <input type="text" name="philHealthNo" value={values.philHealthNo || ''} onChange={handleChange} onKeyDown={numbersWithDash} className={styles.compactInput} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Case & Contact under Commitment */}
              <div className={styles.compactSection}>
                <div className={styles.sectionTitleCompact}>
                  <div className={styles.contactIcon}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  Case & Contact Information
                </div>
                <div className={styles.compactFormGrid}>
                  {/* First Row - Case and contact person info in 4 columns */}
                  <div className={styles.nameRow}>
                    <div className={styles.inputGroup}>
                      <label className={styles.compactLabel}>Case</label>
                      {values.isOtherCase ? (
                        <div style={{ position: 'relative', width: '100%' }}>
                          <input 
                            type="text" 
                            name="case" 
                            value={values.case === 'Others' ? '' : values.case || ''} 
                            onChange={handleOtherCaseInput} 
                            className={styles.compactInput} 
                            placeholder="Type case/offense manually"
                            autoFocus
                            style={{ paddingRight: '2.5rem', width: '100%' }}
                          />
                          <button
                            type="button"
                            onClick={() => onChange && onChange({ ...values, case: '', isOtherCase: false })}
                            style={{
                              position: 'absolute',
                              right: '0.5rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              width: '1.5rem',
                              height: '1.5rem',
                              padding: '0',
                              background: 'rgba(239, 68, 68, 0.2)',
                              border: '1px solid rgba(239, 68, 68, 0.4)',
                              borderRadius: '0.25rem',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '1rem',
                              fontWeight: '700',
                              transition: 'all 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: '1'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                              e.target.style.borderColor = 'rgba(239, 68, 68, 0.6)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                              e.target.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                            }}
                          >
                            &times;
                          </button>
                        </div>
                      ) : (
                        <select 
                          name="case" 
                          value={values.case || ''} 
                          onChange={handleCaseChange} 
                          className={styles.compactInput}
                          style={{ width: '100%', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          <option value="">Select Case/Offense</option>
                          {philippineCriminalCases.map((caseOption, index) => (
                            <option key={index} value={caseOption}>
                              {caseOption}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={styles.compactLabel}>Case No.</label>
                      <input type="text" name="caseNo" value={values.caseNo || ''} onChange={handleChange} className={styles.compactInput} placeholder="e.g., 2024-001" />
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.compactLabel} ${styles.optionalLabel}`}>Name of Contact Person</label>
                      <input type="text" name="contactPerson" value={values.contactPerson || ''} onChange={handleChange} onKeyDown={lettersOnly} className={styles.compactInput} />
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.compactLabel} ${styles.optionalLabel}`}>Relationship</label>
                      <select name="relationship" value={values.relationship || ''} onChange={handleChange} className={styles.compactInput}>
                        <option value="">Select Relationship</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Wife">Wife</option>
                        <option value="Husband">Husband</option>
                        <option value="Mother">Mother</option>
                        <option value="Father">Father</option>
                        <option value="Son">Son</option>
                        <option value="Daughter">Daughter</option>
                        <option value="Brother">Brother</option>
                        <option value="Sister">Sister</option>
                        <option value="Grandmother">Grandmother</option>
                        <option value="Grandfather">Grandfather</option>
                        <option value="Aunt">Aunt</option>
                        <option value="Uncle">Uncle</option>
                        <option value="Cousin">Cousin</option>
                        <option value="Nephew">Nephew</option>
                        <option value="Niece">Niece</option>
                        <option value="Partner">Partner</option>
                        <option value="Friend">Friend</option>
                        <option value="Lawyer">Lawyer</option>
                        <option value="Guardian">Guardian</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Second Row - Address and phone in 2 columns */}
                  <div className={styles.twoColumnRow}>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.compactLabel} ${styles.optionalLabel}`}>Address of Contact Person</label>
                      <input type="text" name="contactAddress" value={values.contactAddress || ''} onChange={handleChange} className={styles.compactInput} />
                    </div>
                    <div className={styles.inputGroup}>
                      <label className={`${styles.compactLabel} ${styles.optionalLabel}`}>Phone / Email</label>
                      <input type="text" name="contactPhone" value={values.contactPhone || ''} onChange={handleChange} className={styles.compactInput} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {/* Right Column - Empty for now or future use */}
          <div className={styles.rightColumn}>
          </div>
        </div>
        
        {/* Camera Modal - Rendered using React Portal to bypass container constraints */}
        {showCamera && ReactDOM.createPortal(
          <div className={styles.cameraModal}>
            <div className={`${styles.cameraModalContent} ${!isDarkMode ? styles.lightMode : ''}`}>
              <div className={styles.cameraModalHeader}>
                <h3 className={styles.cameraModalTitle}>Capture Photo</h3>
                <button 
                  type="button"
                  className={styles.cameraModalClose}
                  onClick={stopCamera}
                >
                  &times;
                </button>
              </div>
              <div className={styles.cameraVideoContainer}>
                <video 
                  ref={videoRef}
                  className={styles.cameraVideo}
                  autoPlay 
                  playsInline
                ></video>
                <canvas 
                  ref={canvasRef}
                  style={{ display: 'none' }}
                ></canvas>
              </div>
              <div className={styles.cameraModalButtons}>
                <button
                  type="button"
                  className={styles.cameraCaptureBtn}
                  onClick={capturePhoto}
                >
                  📸 Capture
                </button>
                <button
                  type="button"
                  className={styles.cameraCancelBtn}
                  onClick={stopCamera}
                >
                  {"\u2716 Cancel"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Underage Confirmation Modal */}
        {showUnderageModal && ReactDOM.createPortal(
          <div 
            className={styles.modalOverlay}
            style={{ zIndex: 99999 }}
            onClick={(e) => {
              // Only close if clicking the overlay itself, not the modal content
              if (e.target === e.currentTarget) {
                setShowUnderageModal(false);
                setPendingDateOfBirth(null);
              }
            }}
          >
            <div 
              className={styles.errorModal} 
              onClick={(e) => e.stopPropagation()} 
              style={{ 
                borderColor: '#f59e0b', 
                maxWidth: '450px',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)'
              }}
            >
              <div className={styles.errorIcon} style={{ background: 'rgba(245, 158, 11, 0.1)', borderColor: 'rgba(245, 158, 11, 0.3)' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2"/>
                  <path d="M12 8v4m0 4h.01" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <h3 style={{ color: '#f59e0b', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️ Underage Person Detected</h3>
              <p style={{ fontSize: '1rem', lineHeight: '1.6', marginBottom: '0.5rem' }}>
                The selected date of birth indicates the person is <strong style={{ color: '#f59e0b', fontSize: '1.1rem' }}>{calculatedAge} years old</strong>, which is under 18 years of age.
              </p>
              <p style={{ fontSize: '0.9rem', marginTop: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>
                Are you sure you want to proceed with this date of birth?
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '1.5rem' }}>
                <button 
                  className={styles.errorBtn} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUnderageModal(false);
                    setPendingDateOfBirth(null);
                  }}
                  style={{ 
                    background: '#6b7280',
                    minWidth: '100px',
                    padding: '0.6rem 1.5rem',
                    fontSize: '0.95rem',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button 
                  className={styles.errorBtn} 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Confirm and apply the date
                    onChange && onChange({
                      ...values,
                      dateOfBirth: pendingDateOfBirth
                    });
                    setShowUnderageModal(false);
                    setPendingDateOfBirth(null);
                  }}
                  style={{ 
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    minWidth: '100px',
                    padding: '0.6rem 1.5rem',
                    fontSize: '0.95rem',
                    fontWeight: '600'
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Foreign Country Info Modal */}
        {showForeignCountryModal && ReactDOM.createPortal(
          <div className={styles.modalOverlay} onClick={() => setShowForeignCountryModal(false)}>
            <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.errorIcon}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="11" x2="12" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="12" y1="7" x2="12.01" y2="7" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>Foreign Country Selected</h3>
              <p style={{ textAlign: 'center', lineHeight: 1.6 }}>
                You selected a country outside the Philippines.<br/>
                <strong>Region, Province, and Municipality</strong> fields are not required for foreign addresses — you may leave them blank.
              </p>
              <button className={styles.errorBtn} onClick={() => setShowForeignCountryModal(false)}>
                Got it
              </button>
            </div>
          </div>,
          document.body
        )}

        {/* Error Modal */}
        {showErrorModal && ReactDOM.createPortal(
          <div className={styles.modalOverlay} onClick={() => setShowErrorModal(false)}>
            <div className={styles.errorModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.errorIcon}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Upload Error</h3>
              <p>{errorMessage}</p>
              <button className={styles.errorBtn} onClick={() => setShowErrorModal(false)}>
                Close
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default HRInfo;